import React, { useEffect, useState } from "react"
import { Modal, Button } from "react-bootstrap"
import { IEventos } from "../../../interfaces/IEventos"
import { TipoEvento } from "../../../interfaces/TipoEvento"
import { formatDateWithDay } from "../../../utils/dateUtils"
import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge"
import SpinnerOverlay from "../../SpinnerOverlay/SpinnerOverlay"
import { Alert, Snackbar, Tab, Tabs, Typography } from "@mui/material"
import { useApi } from "../../../hooks/useApi"
import { API_ENDPOINTS } from "../../../api/endpoints"

interface ConteoIngresosProps {
  show: boolean
  handleClose: () => void
  idEvento: number | null
}

interface ConteoData {
  pagadas: number
  ingresadas: number
  porIngresar: number
  pagadasMenores: number
  ingresadasMenores: number
  porIngresarMenores: number
}

const emptyConteo: ConteoData = {
  pagadas: 0,
  ingresadas: 0,
  porIngresar: 0,
  pagadasMenores: 0,
  ingresadasMenores: 0,
  porIngresarMenores: 0,
}

const ConteoIngresos: React.FC<ConteoIngresosProps> = ({
  show,
  handleClose,
  idEvento,
}) => {
  const [evento, setEvento] = useState<IEventos | null>(null)
  const [conteoCena, setConteoCena] = useState<ConteoData>(emptyConteo)
  const [conteoBaile, setConteoBaile] = useState<ConteoData>(emptyConteo)
  const [conteoGeneral, setConteoGeneral] = useState<ConteoData>(emptyConteo)
  const [loading, setLoading] = useState(false)
  const [tabActivo, setTabActivo] = useState(() => {
    const saved = localStorage.getItem(`conteoTab_${idEvento}`)
    return saved !== null ? parseInt(saved, 10) : 0
  })
  const api = useApi()

  const mapConteo = (data: Record<string, number>): ConteoData => ({
    pagadas: data.totalTarjetasPagadas ?? 0,
    ingresadas: data.totalTarjetasIngresadas ?? 0,
    porIngresar: data.totalTarjetasPorIngresar ?? 0,
    pagadasMenores: data.totalTarjetasPagadasMenores ?? 0,
    ingresadasMenores: data.totalTarjetasIngresadasMenores ?? 0,
    porIngresarMenores: data.totalTarjetasPorIngresarMenores ?? 0,
  })

  const fetchData = async () => {
    if (!idEvento) return
    setLoading(true)
    try {
      const eventoData = await api.get<IEventos>(
        `${API_ENDPOINTS.EVENTOS}/${idEvento}`,
        { showErrorToast: false },
      )
      setEvento(eventoData)

      const tipo = eventoData.tipo
      if (
        tipo === TipoEvento.EGRESADOS ||
        tipo === TipoEvento.CUMPLEANIOS_BODAS
      ) {
        const [cena, baile] = await Promise.all([
          api.get<Record<string, number>>(
            `${API_ENDPOINTS.EGRESADOS}/conteos/${idEvento}?categoria=CENA`,
            { showErrorToast: false },
          ),
          api.get<Record<string, number>>(
            `${API_ENDPOINTS.EGRESADOS}/conteos/${idEvento}?categoria=BAILE`,
            { showErrorToast: false },
          ),
        ])
        setConteoCena(mapConteo(cena))
        setConteoBaile(mapConteo(baile))
      } else {
        const data = await api.get<Record<string, number>>(
          `${API_ENDPOINTS.EGRESADOS}/conteos/${idEvento}`,
          { showErrorToast: false },
        )
        setConteoGeneral(mapConteo(data))
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (show && idEvento) {
      const saved = localStorage.getItem(`conteoTab_${idEvento}`)
      setTabActivo(saved !== null ? parseInt(saved, 10) : 0)
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, idEvento])

  const esDosCategoriasEvento =
    evento?.tipo === TipoEvento.EGRESADOS ||
    evento?.tipo === TipoEvento.CUMPLEANIOS_BODAS

  const renderGauges = (conteo: ConteoData, showMenoresDetail = true) => (
    <>
      <div style={{ display: "flex", gap: "10px" }}>
        <div style={{ width: "450px", height: "180px" }}>
          <Gauge
            value={conteo.ingresadas + conteo.ingresadasMenores}
            startAngle={-110}
            endAngle={110}
            sx={{
              [`& .${gaugeClasses.valueText}`]: {
                fontSize: 34,
                transform: "translate(0px, 0px)",
              },
            }}
            text={({ value, valueMax }) => `${value} / ${valueMax}`}
            valueMax={conteo.pagadas + conteo.pagadasMenores}
          />
        </div>
      </div>
      <div style={{ display: "flex", gap: "20px" }}>
        <div
          style={{
            width: "225px",
            height: "140px",
            textAlign: "center",
            marginBottom: "2rem",
            marginTop: "1rem",
          }}
        >
          <Gauge
            value={conteo.porIngresar + conteo.porIngresarMenores}
            startAngle={0}
            endAngle={360}
            innerRadius="80%"
            outerRadius="100%"
            valueMax={conteo.pagadas + conteo.pagadasMenores}
            sx={() => ({
              [`& .${gaugeClasses.valueText}`]: { fontSize: 34 },
              [`& .${gaugeClasses.valueArc}`]: { fill: "#b20202" },
            })}
          />
          <h5 style={{ color: "#b20202" }}>Tarjetas por Ingresar</h5>
          <h6 style={{ color: "#b20202", fontSize: "13px" }}>
            {showMenoresDetail
              ? `Mayores: ${conteo.porIngresar} - Menores: ${conteo.porIngresarMenores}`
              : `Total: ${conteo.porIngresar}`}
          </h6>
        </div>
        <div
          style={{
            width: "225px",
            height: "140px",
            textAlign: "center",
            marginBottom: "2rem",
            marginTop: "1rem",
          }}
        >
          <Gauge
            value={conteo.ingresadas + conteo.ingresadasMenores}
            startAngle={0}
            endAngle={360}
            innerRadius="80%"
            outerRadius="100%"
            valueMax={conteo.pagadas + conteo.pagadasMenores}
            sx={() => ({
              [`& .${gaugeClasses.valueText}`]: { fontSize: 34 },
              [`& .${gaugeClasses.valueArc}`]: { fill: "#52b202" },
            })}
          />
          <h5 style={{ color: "#52b202" }}>Tarjetas Ingresadas</h5>
          <h6 style={{ color: "#52b202", fontSize: "13px" }}>
            {showMenoresDetail
              ? `Mayores: ${conteo.ingresadas} - Menores: ${conteo.ingresadasMenores}`
              : `Total: ${conteo.ingresadas}`}
          </h6>
        </div>
      </div>
    </>
  )

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="modal-dialog-centered"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {evento
            ? `${evento.nombre} / ${formatDateWithDay(evento.fecha)}`
            : !loading && (
                <Snackbar
                  open={!evento}
                  anchorOrigin={{ vertical: "top", horizontal: "center" }}
                >
                  <Alert severity="warning">
                    {"¡Debe seleccionar un evento!"}
                  </Alert>
                </Snackbar>
              )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <SpinnerOverlay />}

        {!loading && esDosCategoriasEvento && (
          <>
            <Tabs
              value={tabActivo}
              onChange={(_, v) => {
                setTabActivo(v)
                localStorage.setItem(`conteoTab_${idEvento}`, String(v))
              }}
              centered
              sx={{ mb: 1 }}
            >
              <Tab label="Cena" />
              <Tab label="Baile" />
            </Tabs>
            {tabActivo === 0 && renderGauges(conteoCena)}
            {tabActivo === 1 && renderGauges(conteoBaile, false)}
          </>
        )}

        {!loading && !esDosCategoriasEvento && (
          <>
            <Typography
              variant="subtitle2"
              textAlign="center"
              color="text.secondary"
              gutterBottom
            />
            {renderGauges(conteoGeneral)}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="mt-5 d-flex justify-content-center">
        <Button variant="secondary" onClick={handleClose}>
          Cerrar
        </Button>
        <Button variant="primary" onClick={fetchData}>
          Actualizar
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ConteoIngresos
